import React, { useEffect, useState } from "react";
import {
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  DialogActions,
  Button,
  List,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import packageInfo from "../../../package.json";

type Note = { version: string; date?: string; note: string };

export default function ReleaseNotesVersionChip() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch("/releaseNotes.json")
      .then((res) => res.json())
      .then((json) => Array.isArray(json) && setNotes(json))
      .catch(() => {});
  }, []);

  const latest = notes[0];

  const tooltipContent = latest ? (
    <div>
      <Typography component="div" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
        {latest.version}
      </Typography>
      <Typography
        component="div"
        variant="caption"
        sx={{ display: "block", whiteSpace: "normal" }}
      >
        {latest.note}
      </Typography>
    </div>
  ) : (
    <Typography variant="caption">No release notes</Typography>
  );

  return (
    <>
      <Tooltip title={tooltipContent} arrow>
        <Chip
          label={`v ${packageInfo.version}`}
          onClick={() => setOpen(true)}
          color="primary"
          size="small"
          sx={{ maxWidth: 120, margin: "auto", cursor: "pointer" }}
        />
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "50vh",
            borderRadius: 3,
            boxShadow: 6,
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            fontSize: "1.6rem",
            fontWeight: "bold",
            textAlign: "left",
            bgcolor: "background.default",
            color: "white",
            py: 2,
          }}
        >
          Release Notes
        </DialogTitle>

        {/* Content */}
        <DialogContent
          dividers
          sx={{
            height: "calc(50vh - 130px)",
            overflowY: "auto",
            px: 2,
            py: 2,
            bgcolor: "background.default",
          }}
        >
          {notes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No release notes available.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notes.map((n) => (
                <Box key={n.version} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: "100%",
                        bgcolor: "primary.main",
                        borderRadius: 1,
                        mt: 0.5,
                      }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          {`Version ${n.version}`}
                        </Typography>
                        {n.date && (
                          <Typography variant="caption" color="text.secondary">
                            {n.date}
                          </Typography>
                        )}
                      </Stack>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {n.note}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </List>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ justifyContent: "center", py: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
